import React from 'react';
import { ToggleButtonVariant, type ToggleButtonProps } from './types';
import { useToggleButton } from './useToggleButton';

const ToggleButton: React.FC<ToggleButtonProps> = ({
  checked = false,
  onChange,
  label,
  onText,
  offText,
  icon,
  checkedIcon,
  variant = ToggleButtonVariant.Primary,
  size = 'Small',
  state = 'Enabled',
  className = '',
  labelClassName = '',
  disabled,
  onClick,
  onFocus,
  onBlur,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onMouseUp,
  ...props
}) => {
  const {
    config,
    isDisabled,
    internalChecked,
    handleClick,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleMouseUp,
    handleFocus,
    handleBlur,
  } = useToggleButton({
    checked,
    onChange,
    onText,
    offText,
    variant,
    size,
    state,
    disabled,
    onClick,
    onFocus,
    onBlur,
    onMouseEnter,
    onMouseLeave,
    onMouseDown,
    onMouseUp,
  });

  const displayIcon = internalChecked ? checkedIcon || icon : icon;
  const displayLabel = internalChecked ? (onText ?? label) : (offText ?? label);

  return (
    <button
      data-testid="toggle-button"
      aria-label={label}
      type="button"
      role="switch"
      aria-checked={internalChecked}
      disabled={isDisabled}
      onClick={handleClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      className={`inline-flex items-center transition-all duration-200 hover:bg-[#181A1B]/50 rounded ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      style={{
        fontSize: `${config.fontSize}px`,
        lineHeight: `${config.lineHeight}px`,
        fontFamily: 'Roboto',
        fontWeight: size === 'XLarge' ? 400 : 500,
        background: 'transparent',
        border: 'none',
        padding: 0,
        gap: `${config.gap}px`,
      }}
      {...props}
    >
      <div
        data-testid="toggle-button-track"
        aria-label={label}
        className={`relative inline-flex items-center transition-all duration-200 ${isDisabled ? 'opacity-60' : ''}`}
        style={{
          width: '52px',
          height: '32px',
          padding: internalChecked ? '2px 4px' : '2px 8px',
          background: internalChecked ? '#00AB76' : '#E5E7E9',
          borderRadius: '100px',
          border: internalChecked ? 'none' : '2px solid #878B8E',
          justifyContent: internalChecked ? 'flex-end' : 'flex-start',
        }}
      >
        <svg
          width={internalChecked ? '24' : '16'}
          height={internalChecked ? '24' : '16'}
          viewBox={internalChecked ? '0 0 24 24' : '0 0 16 16'}
        >
          <rect
            width={internalChecked ? '24' : '16'}
            height={internalChecked ? '24' : '16'}
            rx={internalChecked ? '12' : '8'}
            fill={internalChecked ? 'white' : '#878B8E'}
          />
        </svg>
      </div>

      {(displayLabel || displayIcon) && (
        <span className="inline-flex items-center gap-2">
          {displayIcon && (
            <span className="inline-flex items-center justify-center shrink-0">
              {displayIcon}
            </span>
          )}
          {displayLabel && (
            <span
              className={`whitespace-nowrap text-sm ${labelClassName}`}
              style={{
                color: '#181A1B',
                fontWeight: 400,
              }}
            >
              {displayLabel}
            </span>
          )}
        </span>
      )}
    </button>
  );
};

export default ToggleButton;
